import { memo, useEffect, useMemo, useRef, useState, type KeyboardEvent, type MouseEvent } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useVirtualizer } from '@tanstack/react-virtual';
import {
  densityOf,
  formatServerClock,
  prefsStore,
  symbolsOf,
  useDisplayZone,
  usePrefsValue,
  useServerOffset,
} from '@adiox/ui';
import { ContextMenu, type MenuItem } from '../chart/ChartOverlays';
import { useStream, useStreamStore } from '../stream/stream';
import { useActiveLogin } from '../trading/useTrading';
import { useWorkspace } from '../workspace/workspaceStore';
import { useLayout } from '../shell/layout';
import { AddSymbolDialog } from './AddSymbolDialog';
import {
  COLUMN_IDS,
  COLUMN_INFO,
  gridTemplate,
  normalizeColumns,
  sortWatchList,
  toggleColumn,
  type ColumnId,
  type SortKey,
} from './columns';
import {
  addSymbol,
  changePercent,
  effectiveWatchList,
  matchesFilter,
  moveSymbol,
  removeSymbol,
  showAll,
  spreadPoints,
} from './marketWatchModel';
import { PriceCell } from './PriceCell';
import { SymbolSpecDialog } from './SymbolSpecDialog';
import { useDayBar, useSymbols, type SymbolInfo } from './useSymbols';

const arrow = { up: '▲', down: '▼', flat: '·' } as const;
type DayBar = { o: number; h: number; l: number } | null;

const Row = memo(function Row({
  info,
  columns,
  template,
  index,
  selected,
  focused,
  dragOver,
  height,
  top,
  onSelect,
  onOpenTicket,
  onMenu,
  onRemove,
  onFocusRow,
  onDragStart,
  onDragOver,
  onDrop,
}: {
  info: SymbolInfo;
  columns: ColumnId[];
  template: string;
  index: number;
  selected: boolean;
  focused: boolean;
  dragOver: boolean;
  height: number;
  top: number;
  onSelect: (name: string) => void;
  onOpenTicket: (name: string) => void;
  onMenu: (name: string, x: number, y: number) => void;
  onRemove: (name: string) => void;
  onFocusRow: (index: number) => void;
  onDragStart: (name: string) => void;
  onDragOver: (index: number) => void;
  onDrop: (index: number) => void;
}) {
  const quote = useStream((s) => s.quotes[info.name]);
  const day = useDayBar(info.name).data ?? null;
  const zone = useDisplayZone();
  const serverOffset = useServerOffset() ?? 0;
  const change = quote ? changePercent(quote.bid, day?.o) : null;
  const closed = quote ? !quote.sessionOpen : false;
  const stale = quote ? !quote.fresh : false;
  const dim = closed || stale || !quote;
  // The daily bar is read now and then; the live bid can already be beyond it.
  const high = day ? Math.max(day.h, quote?.bid ?? day.h) : null;
  const low = day ? Math.min(day.l, quote?.bid ?? day.l) : null;
  const price = (value: number | undefined, label: string) =>
    quote && value !== undefined ? (
      <PriceCell
        value={value}
        digits={info.digits}
        tick={quote.t}
        direction={quote.direction}
        label={label}
      />
    ) : (
      <span className="text-muted">—</span>
    );

  const cell = (id: ColumnId) => {
    switch (id) {
      case 'bid':
        return price(quote?.bid, 'Bid');
      case 'ask':
        return price(quote?.ask, 'Ask');
      case 'spread':
        return (
          <span className="num text-muted">
            {quote ? spreadPoints(quote.bid, quote.ask, info.digits) : '—'}
          </span>
        );
      case 'change':
        return (
          <span
            className={`num ${change === null ? 'text-muted' : change > 0 ? 'text-up' : change < 0 ? 'text-down' : ''}`}
            aria-label="Change today"
          >
            {change === null ? '—' : `${change > 0 ? '+' : ''}${change.toFixed(2)}%`}
          </span>
        );
      case 'high':
        return <span className="num">{high === null ? '—' : high.toFixed(info.digits)}</span>;
      case 'low':
        return <span className="num">{low === null ? '—' : low.toFixed(info.digits)}</span>;
      case 'time':
        // Quote times are on the feed's server clock: back to UTC, then shown in the chosen zone.
        return (
          <span className="num text-muted">
            {quote ? formatServerClock(quote.t - serverOffset * 60_000, zone.offsetAt) : '—'}
          </span>
        );
    }
  };

  return (
    <div
      role="row"
      aria-rowindex={index + 2}
      aria-selected={selected}
      data-symbol={info.name}
      data-row={index}
      tabIndex={focused ? 0 : -1}
      draggable
      onClick={() => onSelect(info.name)}
      onDoubleClick={() => onOpenTicket(info.name)}
      onContextMenu={(e: MouseEvent) => {
        e.preventDefault();
        // The context-menu key has no pointer position: open the menu under the row instead.
        const box = e.currentTarget.getBoundingClientRect();
        const fromKeyboard = e.clientX === 0 && e.clientY === 0;
        onMenu(info.name, fromKeyboard ? box.left + 16 : e.clientX, fromKeyboard ? box.bottom : e.clientY);
      }}
      onFocus={() => onFocusRow(index)}
      onDragStart={(e) => {
        e.dataTransfer.setData('text/plain', info.name);
        e.dataTransfer.effectAllowed = 'move';
        onDragStart(info.name);
      }}
      onDragOver={(e) => {
        e.preventDefault();
        onDragOver(index);
      }}
      onDrop={(e) => {
        e.preventDefault();
        onDrop(index);
      }}
      title="Double-click for a new order, right-click for more"
      style={{ height, transform: `translateY(${top}px)`, gridTemplateColumns: template }}
      className={`group absolute left-0 right-0 grid cursor-default select-none items-center gap-2 border-b border-line/50 px-2 text-sm outline-none ${
        selected ? 'bg-accent/15' : 'hover:bg-panel2'
      } ${dragOver ? 'border-t-2 border-t-accent' : ''} focus-visible:ring-2 focus-visible:ring-accent`}
    >
      <div role="gridcell" className={`flex items-baseline gap-2 truncate ${dim ? 'opacity-60' : ''}`}>
        <span className="font-semibold">{info.name}</span>
        {closed && <span className="rounded bg-panel2 px-1 text-[0.7em] uppercase text-muted">Closed</span>}
        {!closed && stale && (
          <span className="rounded bg-warn/20 px-1 text-[0.7em] uppercase text-warn">Stale</span>
        )}
      </div>
      {columns.map((id) => (
        <div
          key={id}
          role="gridcell"
          className={`text-right ${dim && (id === 'bid' || id === 'ask') ? 'opacity-60' : ''}`}
        >
          {cell(id)}
        </div>
      ))}
      <div role="gridcell" className="flex items-center justify-between">
        <span
          className={`text-xs ${quote?.direction === 'up' ? 'text-up' : quote?.direction === 'down' ? 'text-down' : 'text-muted'}`}
          aria-label={`Last move ${quote?.direction ?? 'none'}`}
        >
          {arrow[quote?.direction ?? 'flat']}
        </span>
        <button
          type="button"
          tabIndex={-1}
          aria-label={`Remove ${info.name}`}
          title={`Hide ${info.name}`}
          onClick={(e) => {
            e.stopPropagation();
            onRemove(info.name);
          }}
          // Shown on hover, on the selected row, and always on touch screens (which have no hover).
          className={`rounded px-1.5 text-muted hover:text-down focus-visible:opacity-100 group-hover:opacity-100 [@media(hover:none)]:opacity-100 ${
            selected ? 'opacity-100' : 'opacity-0'
          }`}
        >
          ×
        </button>
      </div>
    </div>
  );
});

/**
 * Live quotes for the symbols the user follows: a virtualised, keyboard-operable grid whose list, order and columns are part
 * of their synced preferences. Right-click (or the context-menu key, or a long press) opens New order, Open chart,
 * Specification and Hide; double-click opens the order ticket.
 */
export function MarketWatch({
  columns: fixedColumns,
  classic = false,
}: {
  /** Columns to show instead of the user's choice (the classic Details tab shows them all). */
  columns?: ColumnId[];
  /** The classic terminal's look: no filter bar; a "click to add" row under the list instead. */
  classic?: boolean;
} = {}) {
  const store = useStreamStore();
  const queryClient = useQueryClient();
  const symbols = useSymbols();
  const savedRaw = usePrefsValue((s) => s.data.marketWatch); // a stable reference; symbolsOf builds a new array
  const saved = useMemo(() => symbolsOf({ marketWatch: savedRaw }), [savedRaw]);
  const columnsRaw = usePrefsValue((s) => s.data.marketWatchColumns);
  const columns = useMemo(() => fixedColumns ?? normalizeColumns(columnsRaw), [fixedColumns, columnsRaw]);
  const template = gridTemplate(columns);
  const density = usePrefsValue((s) => densityOf(s.data));
  const activeSymbol = useWorkspace((s) => s.activeSymbol);
  const select = useWorkspace((s) => s.select);
  const actions = useWorkspace((s) => s.actions);
  const { mobile } = useLayout();
  const login = useActiveLogin();
  const account = useStream((s) => (login === undefined ? undefined : s.accounts[login]));
  const [filter, setFilter] = useState('');
  const [adding, setAdding] = useState(false);
  const [focusIndex, setFocusIndex] = useState(0);
  const [dragging, setDragging] = useState<string | null>(null);
  const [overIndex, setOverIndex] = useState<number | null>(null);
  const [sorted, setSorted] = useState<{ key: SortKey; dir: 'asc' | 'desc' } | null>(null);
  const [menu, setMenu] = useState<{ x: number; y: number; title: string; items: MenuItem[] } | null>(null);
  const [spec, setSpec] = useState<SymbolInfo | null>(null);
  const scroller = useRef<HTMLDivElement>(null);
  const columnsButton = useRef<HTMLButtonElement>(null);

  const available = useMemo(() => symbols.data ?? [], [symbols.data]);
  const byName = useMemo(() => new Map(available.map((s) => [s.name, s])), [available]);
  const list = useMemo(() => effectiveWatchList(saved, available), [saved, available]);
  const visible = useMemo(
    () => list.filter((n) => matchesFilter(byName.get(n), n, filter)).map((n) => byName.get(n)!),
    [list, byName, filter],
  );
  // The classic look has the terminal's tight list rows.
  const rowHeight = classic ? 19 : density === 'compact' ? 26 : 34;

  // The quote stream follows the list; reordering does not resubscribe (the key ignores the order).
  const subscriptionKey = useMemo(() => [...list].sort().join(','), [list]);
  useEffect(() => {
    const names = subscriptionKey ? subscriptionKey.split(',') : [];
    if (names.length > 0) void store.subscribeQuotes(names);
    return () => {
      if (names.length > 0) void store.unsubscribeQuotes(names);
    };
  }, [store, subscriptionKey]);

  // eslint-disable-next-line react-hooks/incompatible-library -- the virtualiser is used directly, never memoised
  const virtual = useVirtualizer({
    count: visible.length,
    getScrollElement: () => scroller.current,
    estimateSize: () => rowHeight,
    overscan: 8,
    initialRect: { width: 400, height: 600 },
  });
  useEffect(() => virtual.measure(), [virtual, rowHeight]);

  const save = (next: string[]) => {
    setSorted(null); // a manual change leaves any sort behind
    prefsStore.set('marketWatch', { symbols: next });
  };
  const remove = (name: string) => save(removeSymbol(list, name));
  const hiddenCount = available.length - list.length;
  const openTicket = (name: string) => {
    select(name);
    actions?.orderTicket();
  };
  const focusRow = (index: number) => {
    const clamped = Math.max(0, Math.min(visible.length - 1, index));
    setFocusIndex(clamped);
    virtual.scrollToIndex(clamped);
    requestAnimationFrame(() =>
      scroller.current?.querySelector<HTMLElement>(`[data-row="${clamped}"]`)?.focus(),
    );
  };

  /** Sorts the list once by a column (like MT5): a second click on the same column reverses it. */
  const sortBy = (key: SortKey) => {
    const dir = sorted?.key === key && sorted.dir === 'asc' ? 'desc' : 'asc';
    const quotes = store.getState().quotes;
    const valueOf = (name: string, k: SortKey): number | string | null => {
      const q = quotes[name];
      const info = byName.get(name);
      const day = queryClient.getQueryData<DayBar>(['day-bar', name]) ?? null;
      switch (k) {
        case 'symbol':
          return name;
        case 'bid':
          return q?.bid ?? null;
        case 'ask':
          return q?.ask ?? null;
        case 'spread':
          return q && info ? spreadPoints(q.bid, q.ask, info.digits) : null;
        case 'change':
          return q ? changePercent(q.bid, day?.o) : null;
        case 'high':
          return day ? Math.max(day.h, q?.bid ?? day.h) : null;
        case 'low':
          return day ? Math.min(day.l, q?.bid ?? day.l) : null;
        case 'time':
          return q?.t ?? null;
      }
    };
    prefsStore.set('marketWatch', { symbols: sortWatchList(list, key, dir, valueOf) });
    setSorted({ key, dir });
  };

  const columnItems = (): MenuItem[] =>
    COLUMN_IDS.map((id) => ({
      key: `col-${id}`,
      label: COLUMN_INFO[id].label,
      checked: columns.includes(id),
      onSelect: () => prefsStore.set('marketWatchColumns', toggleColumn(columns, id)),
    }));

  const rowMenu = (name: string, x: number, y: number) => {
    select(name);
    setMenu({
      x,
      y,
      title: name,
      items: [
        { label: 'New order', shortcut: 'F9', onSelect: () => openTicket(name) },
        { label: 'Open chart', onSelect: () => actions?.newChart(name) },
        ...(actions?.showPanel
          ? [{ label: 'Tick chart', onSelect: () => actions.showPanel?.('ticks') }]
          : []),
        { label: 'Specification', onSelect: () => setSpec(byName.get(name) ?? null) },
        { label: 'Hide', shortcut: 'Del', danger: true, onSelect: () => remove(name) },
        {
          label: hiddenCount > 0 ? `Show all (${hiddenCount} hidden)` : 'Show all',
          disabled: hiddenCount <= 0,
          onSelect: () => save(showAll(list, available)),
        },
        { label: 'Columns…', onSelect: () => setMenu({ x, y, title: 'Columns', items: columnItems() }) },
      ],
    });
  };

  const onKeyDown = (e: KeyboardEvent) => {
    const info = visible[focusIndex];
    if (!info) return;
    if (e.altKey && (e.key === 'ArrowUp' || e.key === 'ArrowDown')) {
      e.preventDefault();
      const delta = e.key === 'ArrowUp' ? -1 : 1;
      const neighbour = visible[focusIndex + delta];
      if (neighbour) save(moveSymbol(list, info.name, list.indexOf(neighbour.name)));
      focusRow(focusIndex + delta);
    } else if (e.key === 'ArrowDown') {
      e.preventDefault();
      focusRow(focusIndex + 1);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      focusRow(focusIndex - 1);
    } else if (e.key === 'Home') {
      e.preventDefault();
      focusRow(0);
    } else if (e.key === 'End') {
      e.preventDefault();
      focusRow(visible.length - 1);
    } else if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      select(info.name);
    } else if (e.key === 'Delete') {
      e.preventDefault();
      remove(info.name);
      focusRow(Math.min(focusIndex, visible.length - 2));
    }
  };

  const status = symbols.isPending ? 'Loading symbols…' : symbols.isError ? 'Could not load symbols.' : null;
  const header = (key: SortKey, text: string, title: string, right: boolean) => (
    <div
      key={key}
      role="columnheader"
      aria-sort={sorted?.key === key ? (sorted.dir === 'asc' ? 'ascending' : 'descending') : undefined}
      className={right ? 'text-right' : ''}
    >
      <button
        type="button"
        title={`${title}. Click to sort the list by it.`}
        onClick={() => sortBy(key)}
        className="uppercase tracking-wide hover:text-fg"
      >
        {text}
        <span aria-hidden="true">{sorted?.key === key ? (sorted.dir === 'asc' ? ' ▲' : ' ▼') : ''}</span>
      </button>
    </div>
  );

  return (
    <section className="flex h-full flex-col bg-panel" aria-label="Market Watch">
      <div className="flex items-center gap-2 border-b border-line p-2" hidden={classic}>
        <label className="sr-only" htmlFor="market-watch-filter">
          Filter watched symbols
        </label>
        <input
          id="market-watch-filter"
          type="search"
          placeholder="Filter"
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          className="min-w-0 flex-1 rounded-md border border-line bg-panel2 px-2 py-1 outline-none focus:border-accent"
        />
        <button
          ref={columnsButton}
          type="button"
          aria-haspopup="menu"
          onClick={() => {
            const box = columnsButton.current!.getBoundingClientRect();
            setMenu({ x: box.left, y: box.bottom + 4, title: 'Columns', items: columnItems() });
          }}
          className="rounded-md border border-line px-2 py-1 text-sm hover:border-accent"
        >
          Columns
        </button>
        <button
          type="button"
          onClick={() => setAdding(true)}
          className="rounded-md bg-accent px-3 py-1 font-semibold text-accent-fg hover:brightness-110"
        >
          + Add
        </button>
      </div>

      {status && <p className="p-4 text-sm text-muted">{status}</p>}
      {!status && list.length === 0 && (
        <p className="p-4 text-sm text-muted">
          Nothing to watch yet. Use “Add” to follow symbols, or{' '}
          <button
            type="button"
            className="text-accent underline-offset-2 hover:underline"
            onClick={() => save(showAll(list, available))}
          >
            show all
          </button>
          .
        </p>
      )}

      {!status && list.length > 0 && (
        <div
          role="grid"
          aria-label="Quotes"
          aria-rowcount={visible.length + 1}
          onKeyDown={onKeyDown}
          className="flex min-h-0 flex-1 flex-col"
        >
          <div
            role="row"
            aria-rowindex={1}
            style={{ gridTemplateColumns: template }}
            className="grid gap-2 border-b border-line px-2 py-1 text-xs uppercase tracking-wide text-muted"
          >
            {header('symbol', 'Symbol', 'Symbol name', false)}
            {columns.map((id) => header(id, COLUMN_INFO[id].header, COLUMN_INFO[id].title, true))}
            <div role="columnheader" aria-label="Direction and actions" />
          </div>
          <div ref={scroller} className="min-h-0 flex-1 overflow-auto" data-testid="market-watch-rows">
            <div style={{ height: virtual.getTotalSize() }} className="relative w-full">
              {virtual.getVirtualItems().map((item) => {
                const info = visible[item.index]!;
                return (
                  <Row
                    key={info.name}
                    info={info}
                    columns={columns}
                    template={template}
                    index={item.index}
                    selected={activeSymbol === info.name}
                    focused={item.index === focusIndex}
                    dragOver={dragging !== null && overIndex === item.index}
                    height={item.size}
                    top={item.start}
                    onSelect={(name) => {
                      select(name);
                      // On a phone there is no double-click or right-click: a tap opens the symbol's menu.
                      if (!mobile) return;
                      const box = scroller.current
                        ?.querySelector<HTMLElement>(`[data-symbol="${name}"]`)
                        ?.getBoundingClientRect();
                      if (box) rowMenu(name, box.left + 16, box.bottom);
                    }}
                    onOpenTicket={openTicket}
                    onMenu={rowMenu}
                    onRemove={remove}
                    onFocusRow={setFocusIndex}
                    onDragStart={setDragging}
                    onDragOver={setOverIndex}
                    onDrop={(index) => {
                      const target = visible[index];
                      if (dragging && target) save(moveSymbol(list, dragging, list.indexOf(target.name)));
                      setDragging(null);
                      setOverIndex(null);
                    }}
                  />
                );
              })}
            </div>
          </div>
        </div>
      )}
      {classic && !status && (
        <button
          type="button"
          onClick={() => setAdding(true)}
          className="w-full shrink-0 px-2 py-0.5 text-left text-muted hover:bg-panel2"
          data-testid="market-watch-add"
        >
          + click to add
        </button>
      )}

      {menu && (
        <ContextMenu
          x={menu.x}
          y={menu.y}
          title={menu.title}
          items={menu.items}
          onClose={() => setMenu(null)}
        />
      )}
      <AddSymbolDialog
        open={adding}
        available={available}
        watched={list}
        onAdd={(name) => save(addSymbol(list, name))}
        onClose={() => setAdding(false)}
      />
      <SymbolSpecDialog
        info={spec}
        leverage={account?.leverage ?? 0}
        currency={account?.currency ?? ''}
        onClose={() => setSpec(null)}
      />
    </section>
  );
}
